'use client';

import { Button } from '@/components/button';
import { SvgIcon } from '@/components/svgIcon';
import { SwdCollapse } from '@/components/swdCollapse/SwdCollapse';
import { trimSlashes } from '@/helper/menuUtils';
import { linkToString } from '@/helper/urls';
import ChevronDown from '@/public/icons/chevron-s-down.svg';
import classNames from 'classnames';
import { Fragment, useEffect, useState } from 'react';

import type { CustomStoryblokLinkResource } from '../list';
import classes from '../list.module.scss';

export default function SGListGroup({
  story,
  currentPathName,
}: {
  story: CustomStoryblokLinkResource;
  currentPathName: string;
}) {
  const [open, setOpen] = useState(false);

  const hasActiveDescendant = (item: CustomStoryblokLinkResource, currentPath: string): boolean => {
    return item.children.some((child) => {
      if (trimSlashes(child.slug) === trimSlashes(currentPath)) return true;
      if (child.children && child.children.length > 0) {
        return hasActiveDescendant(child, currentPath);
      }
      return false;
    });
  };

  useEffect(() => {
    if (hasActiveDescendant(story, currentPathName)) {
      setOpen(true);
    }
  }, [story, currentPathName]);

  return (
    <Fragment>
      <li>
        <Button
          variant="text"
          data-testid="style-guide-side-navigation-group-header"
          aria-label={story.name}
          className={classes.navigationItem}
          onClick={(event) => {
            event.stopPropagation();
            setOpen(!open);
          }}
        >
          <span className={classes.listItemText}>{story.name}</span>
          <SvgIcon
            viewBox="0 0 16 16"
            component={ChevronDown}
            className={classNames(open ? classes.open : '', classes.dropdownIcon)}
          />
        </Button>
      </li>

      <SwdCollapse in={open}>
        <ul data-testid="style-guide-side-navigation-group-list" className={classes.nestedList}>
          {story.children.map((child, index) => (
            <Fragment key={child.id}>
              {child.is_folder ? (
                <SGListGroup story={child} currentPathName={currentPathName} />
              ) : (
                <li data-testid={'style-guide-side-navigation-group-list-item' + Number(index + 1)} key={child.id}>
                  <Button
                    variant="text"
                    data-testid={'style-guide-side-navigation-group-list-item' + Number(index + 1) + '-link'}
                    href={linkToString(child.slug)}
                    className={classNames(classes.navigationItem, {
                      [classes.selected!]: trimSlashes(child.slug) === trimSlashes(currentPathName),
                    })}
                    aria-current={trimSlashes(child.slug) === trimSlashes(currentPathName)}
                  >
                    <span className={classes.listItemText}>{child.name}</span>
                  </Button>
                </li>
              )}
            </Fragment>
          ))}
        </ul>
      </SwdCollapse>
    </Fragment>
  );
}
